import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Loader2, ArrowLeft, MapPin, Briefcase, Building2, Mail, Upload, CheckCircle2, UserPlus } from "lucide-react";
import SEO from "@/components/SEO";

function PageHeader({ eyebrow, title, sub }) {
  return (
    <div className="bg-gradient-to-b from-emerald-50/40 to-white border-b border-slate-200">
      <div className="nc-container py-12 md:py-16 max-w-4xl">
        {eyebrow && <span className="nc-chip">{eyebrow}</span>}
        <h1 className="mt-4 text-3xl md:text-5xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: "Poppins" }}>{title}</h1>
        {sub && <p className="mt-4 text-slate-600 text-lg leading-relaxed max-w-3xl">{sub}</p>}
      </div>
    </div>
  );
}

const RESUME_ACCEPT = ".pdf,.doc,.docx,.rtf,.txt,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/rtf,text/plain";
const RESUME_MAX_BYTES = 8 * 1024 * 1024;

function ApplyForm({ endpoint, roleTitle }) {
  const { t } = useI18n();
  const BLANK = {
    full_name: "", email: "", mobile: "", location: "",
    experience_years: "", current_role: "", cover_letter: "",
  };
  const [form, setForm] = useState(BLANK);
  const [resume, setResume] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  const fileRef = useRef(null);

  const f = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onResume = (e) => {
    const file = e.target.files?.[0];
    if (!file) { setResume(null); return; }
    if (file.size > RESUME_MAX_BYTES) {
      setErr("Resume must be under 8 MB");
      e.target.value = "";
      return;
    }
    setErr("");
    setResume(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.full_name.trim() || !form.email.trim() || !form.mobile.trim()) {
      setErr("Name, email and mobile are required");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => { if (v) fd.append(k, v); });
      if (resume) fd.append("resume", resume);
      await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDone(true);
      setForm(BLANK);
      setResume(null);
      if (fileRef.current) fileRef.current.value = "";
    } catch (e2) {
      const d = e2.response?.data?.detail;
      setErr(typeof d === "string" ? d : (d?.[0]?.msg || e2.message));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="rounded-2xl border border-emerald-200 bg-emerald-50/60 p-6" data-testid="apply-success">
        <div className="flex items-start gap-3">
          <CheckCircle2 className="w-6 h-6 text-emerald-600 flex-shrink-0" />
          <div className="flex-1">
            <h3 className="font-semibold text-emerald-900" style={{ fontFamily: "Poppins" }}>{t("careers.applySuccess")}</h3>
            <button
              type="button"
              onClick={() => setDone(false)}
              className="mt-3 text-sm font-medium text-emerald-700 underline"
              data-testid="apply-again"
            >
              {t("careers.applyAnother")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="rounded-2xl border border-slate-200 bg-white p-6 nc-soft-shadow" data-testid="apply-form">
      <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "Poppins" }}>
        {roleTitle ? t("careers.applyTitle") : t("careers.openAppTitle")}
      </h3>
      <p className="text-sm text-slate-600 mt-1">{roleTitle ? t("careers.applyIntro") : t("careers.openAppSub")}</p>

      <div className="mt-5 grid sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-medium text-slate-600">{t("careers.fFullName")} *</label>
          <input className="nc-input mt-1" value={form.full_name} onChange={f("full_name")} required data-testid="apply-name" />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600">{t("careers.fEmail")} *</label>
          <input type="email" className="nc-input mt-1" value={form.email} onChange={f("email")} required data-testid="apply-email" />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600">{t("careers.fMobile")} *</label>
          <input className="nc-input mt-1" value={form.mobile} onChange={f("mobile")} required data-testid="apply-mobile" />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600">{t("careers.fLocation")}</label>
          <input className="nc-input mt-1" value={form.location} onChange={f("location")} data-testid="apply-location" />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600">{t("careers.fExperience")}</label>
          <input className="nc-input mt-1" value={form.experience_years} onChange={f("experience_years")} placeholder="e.g. 4" data-testid="apply-experience" />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600">{t("careers.fCurrentRole")}</label>
          <input className="nc-input mt-1" value={form.current_role} onChange={f("current_role")} data-testid="apply-current-role" />
        </div>
      </div>

      <div className="mt-3">
        <label className="text-xs font-medium text-slate-600">{t("careers.fCoverLetter")}</label>
        <textarea className="nc-input mt-1" rows={4} value={form.cover_letter} onChange={f("cover_letter")} data-testid="apply-cover" />
      </div>

      <div className="mt-4">
        <label className="text-xs font-medium text-slate-600">{t("careers.fResume")}</label>
        <div className="mt-1 flex items-center gap-3">
          <label className="inline-flex items-center gap-2 cursor-pointer px-3 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 border border-slate-200 text-sm">
            <Upload className="w-4 h-4" /> {t("careers.fResumeChoose")}
            <input
              ref={fileRef}
              type="file"
              accept={RESUME_ACCEPT}
              className="hidden"
              onChange={onResume}
              data-testid="apply-resume"
            />
          </label>
          <span className="text-sm text-slate-600 truncate max-w-[60%]" data-testid="apply-resume-name">
            {resume ? resume.name : t("careers.fResumeNone")}
          </span>
        </div>
      </div>

      {err && (
        <div className="mt-4 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-sm px-3 py-2" data-testid="apply-error">
          {err}
        </div>
      )}

      <button
        type="submit"
        disabled={submitting}
        className="mt-5 w-full justify-center nc-btn-primary disabled:opacity-60"
        data-testid="apply-submit"
      >
        {submitting ? (<><Loader2 className="w-4 h-4 animate-spin" /> {t("careers.submitting")}</>) : t("careers.submitApply")}
      </button>
    </form>
  );
}

export function CareerList() {
  const { t } = useI18n();
  const [jobs, setJobs] = useState(null);
  const [openApp, setOpenApp] = useState(false);

  useEffect(() => {
    api.get("/careers")
      .then(({ data }) => setJobs(data))
      .catch(() => setJobs([]));
  }, []);

  return (
    <div data-testid="careers-list">
      <SEO title="Careers" description="Open roles at NakshaCart / MECHACIV Construction Pvt Ltd." path="/careers" />
      <PageHeader title={t("careers.title")} sub={t("careers.sub")} />
      <div className="nc-container py-10 max-w-4xl">
        {jobs === null && (
          <div className="py-16 flex justify-center text-slate-400"><Loader2 className="animate-spin" /></div>
        )}
        {jobs && jobs.length === 0 && (
          <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center text-slate-500">
            {t("careers.empty")}
          </div>
        )}
        {jobs && jobs.length > 0 && (
          <ul className="space-y-4">
            {jobs.map((j) => (
              <li key={j.id}>
                <Link
                  to={`/careers/${j.slug}`}
                  className="block bg-white rounded-2xl border border-slate-200 p-5 md:p-6 hover:border-emerald-400 hover:shadow-sm transition-all"
                  data-testid={`career-card-${j.slug}`}
                >
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "Poppins" }}>{j.title}</h3>
                      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1.5 text-sm text-slate-600">
                        {j.department && (
                          <span className="inline-flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5 text-slate-400" />{j.department}</span>
                        )}
                        {j.location && (
                          <span className="inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-slate-400" />{j.location}</span>
                        )}
                        {j.employment_type && (
                          <span className="inline-flex items-center gap-1.5"><Briefcase className="w-3.5 h-3.5 text-slate-400" />{j.employment_type}</span>
                        )}
                      </div>
                    </div>
                    <span className="text-sm font-medium text-emerald-700 self-start md:self-auto">{t("careers.readMore")} →</span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}

        {jobs && (
          <div className="mt-10 rounded-2xl border border-dashed border-slate-300 bg-slate-50/60 p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0">
                <UserPlus className="w-5 h-5" />
              </div>
              <div>
                <div className="font-medium text-slate-900">{t("careers.orOpenApp")}</div>
                <div className="text-sm text-slate-600">{t("careers.openAppSub")}</div>
              </div>
            </div>
            <button
              onClick={() => setOpenApp((v) => !v)}
              className="nc-btn-secondary self-start"
              data-testid="toggle-open-app"
            >
              {openApp ? "Close" : t("careers.openAppCta")}
            </button>
          </div>
        )}

        {openApp && (
          <div className="mt-6" data-testid="open-app-section">
            <ApplyForm endpoint="/careers/apply" roleTitle="" />
          </div>
        )}
      </div>
    </div>
  );
}

export function CareerDetail() {
  const { t, lang } = useI18n();
  const { slug } = useParams();
  const [job, setJob] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/careers/${slug}`)
      .then(({ data }) => setJob(data))
      .catch(() => setError(true));
  }, [slug]);

  if (error) {
    return (
      <div className="nc-container py-16 max-w-3xl text-center">
        <p className="text-slate-500">{t("careers.empty")}</p>
        <Link to="/careers" className="text-emerald-700 font-medium mt-4 inline-block">← {t("careers.backToCareers")}</Link>
      </div>
    );
  }
  if (!job) return <div className="min-h-[40vh] flex items-center justify-center"><Loader2 className="animate-spin" /></div>;

  const dateLocale = lang === "hi" ? "hi-IN" : "en-IN";

  return (
    <div className="bg-white" data-testid="career-detail">
      <SEO
        title={job.title}
        description={job.description?.slice(0, 160)}
        path={`/careers/${job.slug}`}
      />
      <div className="nc-container py-8 max-w-3xl">
        <Link to="/careers" className="text-sm text-slate-500 inline-flex items-center gap-1.5">
          <ArrowLeft className="w-4 h-4" /> {t("careers.backToCareers")}
        </Link>

        <h1 className="mt-4 text-3xl md:text-4xl font-semibold text-slate-900" style={{ fontFamily: "Poppins" }}>{job.title}</h1>

        <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-sm text-slate-600">
          {job.department && (
            <span className="inline-flex items-center gap-1.5"><Building2 className="w-4 h-4 text-slate-400" />{job.department}</span>
          )}
          {job.location && (
            <span className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4 text-slate-400" />{job.location}</span>
          )}
          {job.employment_type && (
            <span className="inline-flex items-center gap-1.5"><Briefcase className="w-4 h-4 text-slate-400" />{job.employment_type}</span>
          )}
        </div>
        {job.posted_at && (
          <p className="text-xs text-slate-400 mt-3">{t("careers.posted")}: {new Date(job.posted_at).toLocaleDateString(dateLocale)}</p>
        )}

        <h2 className="mt-8 text-xl font-semibold text-slate-900" style={{ fontFamily: "Poppins" }}>{t("careers.jobDescription")}</h2>
        <div className="mt-3 prose prose-slate max-w-none">
          {job.description.split("\n\n").map((p, i) => (
            <p key={i} className="text-slate-700 leading-relaxed mb-4 whitespace-pre-line">{p}</p>
          ))}
        </div>

        <div className="mt-10">
          <ApplyForm endpoint={`/careers/${job.slug}/apply`} roleTitle={job.title} />
          {job.apply_email && (
            <p className="mt-4 text-xs text-slate-500">
              {t("careers.applyVia")}{" "}
              <a href={`mailto:${job.apply_email}?subject=${encodeURIComponent(job.title)}`} className="inline-flex items-center gap-1 text-emerald-700 font-medium">
                <Mail className="w-3.5 h-3.5" />{job.apply_email}
              </a>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
